import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { MobileNav, DesktopNav } from './components/layout/Navigation';

// Auth Pages (eagerly loaded — small, critical path)
import LoginPage from './pages/auth/LoginPage';
import CompleteProfilePage from './pages/auth/CompleteProfilePage';
import KYCUploadPage from './pages/auth/KYCUploadPage';

// All other pages lazy-loaded for fast initial bundle
const HomePage = lazy(() => import('./pages/home/HomePage'));
const ProductsPage = lazy(() => import('./pages/products/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/products/ProductDetailPage'));

// Lazy-loaded pages
const CategoryPage = lazy(() => import('./pages/products/CategoryPage'));
const CartPage = lazy(() => import('./pages/cart/CartPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'));
const StatementPage = lazy(() => import('./pages/statement/StatementPage'));
const WalletsPage = lazy(() => import('./pages/wallets/WalletsPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));

// Admin Pages (lazy)
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminForgotPasswordPage = lazy(() => import('./pages/admin/AdminForgotPasswordPage'));
const AdminResetPasswordPage = lazy(() => import('./pages/admin/AdminResetPasswordPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminDealers = lazy(() => import('./pages/admin/AdminDealers'));
const AdminDealerDetail = lazy(() => import('./pages/admin/AdminDealerDetail'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminEmployees = lazy(() => import('./pages/admin/AdminEmployees'));
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminReturns = lazy(() => import('./pages/admin/AdminReturns'));
const AdminHomepage = lazy(() => import('./pages/admin/AdminHomepage'));
const AdminCombos = lazy(() => import('./pages/admin/AdminCombos'));
const AdminSchemes = lazy(() => import('./pages/admin/AdminSchemes'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminSchemeAnalytics = lazy(() => import('./pages/admin/AdminSchemeAnalytics'));
const AdminCouponAnalytics = lazy(() => import('./pages/admin/AdminCouponAnalytics'));
const AdminLoyaltySchemes = lazy(() => import('./pages/admin/AdminLoyaltySchemes'));
const AdminFinance = lazy(() => import('./pages/admin/AdminFinance'));
const AdminClientErrors = lazy(() => import('./pages/admin/AdminClientErrors'));
const AdminCnfAgents = lazy(() => import('./pages/admin/AdminCnfAgents'));
const AdminAccountDeletionRequests = lazy(() => import('./pages/admin/AdminAccountDeletionRequests'));
const AdminMarketingBudget = lazy(() => import('./pages/admin/AdminMarketingBudget'));
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));

// Employee Pages (lazy)
const EmployeeLoginPage = lazy(() => import('./pages/employee/EmployeeLoginPage'));
const EmployeeLayout = lazy(() => import('./pages/employee/EmployeeLayout'));
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'));
const EmployeeDealers = lazy(() => import('./pages/employee/EmployeeDealers'));
const EmployeeDealerDetail = lazy(() => import('./pages/employee/EmployeeDealerDetail'));
const EmployeeOrders = lazy(() => import('./pages/employee/EmployeeOrders'));
const EmployeePlaceOrder = lazy(() => import('./pages/employee/EmployeePlaceOrder'));
const EmployeeEditOrder = lazy(() => import('./pages/employee/EmployeeEditOrder'));
const EmployeePendingApprovals = lazy(() => import('./pages/employee/EmployeePendingApprovals'));
const EmployeeIncentives = lazy(() => import('./pages/employee/EmployeeIncentives'));
const EmployeeTeam = lazy(() => import('./pages/employee/EmployeeTeam'));
const FieldForcePages = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: { ...m } })));
const EmployeeAttendance = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeAttendance })));
const EmployeeLiquidation = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeLiquidation })));
const EmployeeFieldVisits = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeFieldVisits })));
const EmployeeShopVisits = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeShopVisits })));
const EmployeeStocks = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeStocks })));
const EmployeeMyTargets = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeMyTargets })));
const EmployeeSpecialIncentive = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeSpecialIncentive })));
const EmployeeActivityRequests = lazy(() => import('./pages/employee/FieldForcePages').then(m => ({ default: m.EmployeeActivityRequests })));
const EmployeeCnfDashboard = lazy(() => import('./pages/employee/EmployeeCnfDashboard'));
const AdminEmployeeManagement = lazy(() => import('./pages/admin/AdminEmployeeManagement'));

// Other lazy pages
const OrderApprovalPage = lazy(() => import('./pages/orders/OrderApprovalPage'));
const CreditRepaymentPage = lazy(() => import('./pages/credit/CreditRepaymentPage'));
const SpecialDetailPage = lazy(() => import('./pages/home/SpecialDetailPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const BankPaymentPage = lazy(() => import('./pages/orders/BankPaymentPage'));
const ReturnsPage = lazy(() => import('./pages/orders/ReturnsPage'));
const OffersPage = lazy(() => import('./pages/offers/OffersPage'));

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// User Layout with Navigation
const UserLayout = ({ children }) => {
  return (
    <>
      <DesktopNav />
      {children}
      <MobileNav />
    </>
  );
};

const LazyFallback = () => (
  <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
    <div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" />
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Protected User Routes */}
      <Route path="/complete-profile" element={
        <ProtectedRoute>
          <CompleteProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/kyc-upload" element={
        <ProtectedRoute>
          <KYCUploadPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <UserLayout>
            <HomePage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/products" element={
        <ProtectedRoute>
          <UserLayout>
            <ProductsPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/products/:id" element={
        <ProtectedRoute>
          <ProductDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/category/:slug" element={
        <ProtectedRoute>
          <CategoryPage />
        </ProtectedRoute>
      } />

      <Route path="/specials/:id" element={
        <ProtectedRoute>
          <SpecialDetailPage />
        </ProtectedRoute>
      } />

      
      <Route path="/cart" element={
        <ProtectedRoute>
          <UserLayout>
            <CartPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/orders" element={
        <ProtectedRoute>
          <UserLayout>
            <OrdersPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/orders/:id" element={
        <ProtectedRoute>
          <UserLayout>
            <OrderDetailPage />
          </UserLayout>
        </ProtectedRoute>
      } />

      <Route path="/orders/:orderId/bank-payment" element={
        <ProtectedRoute>
          <UserLayout>
            <BankPaymentPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/statement" element={
        <ProtectedRoute>
          <UserLayout>
            <StatementPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/wallets" element={
        <ProtectedRoute>
          <UserLayout>
            <WalletsPage />
          </UserLayout>
        </ProtectedRoute>
      } />

      <Route path="/offers" element={
        <ProtectedRoute>
          <UserLayout>
            <OffersPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/notifications" element={
        <ProtectedRoute>
          <UserLayout>
            <NotificationsPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/credit/repay" element={
        <ProtectedRoute>
          <CreditRepaymentPage />
        </ProtectedRoute>
      } />

      
      <Route path="/profile" element={
        <ProtectedRoute>
          <UserLayout>
            <ProfilePage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/returns" element={
        <ProtectedRoute>
          <UserLayout>
            <ReturnsPage />
          </UserLayout>
        </ProtectedRoute>
      } />
      
      {/* Order Approval (public - no auth required) */}
      <Route path="/orders/:orderId/approve" element={<OrderApprovalPage />} />

      {/* Public legal pages — required for Play Store: privacy, delete-account, etc. */}
      <Route path="/legal/:slug" element={<LegalPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/fuckedup" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="dealers" element={<AdminDealers />} />
        <Route path="dealers/:id" element={<AdminDealerDetail />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="returns" element={<AdminReturns />} />
        <Route path="employees" element={<AdminEmployees />} />
        <Route path="employee-management" element={<AdminEmployeeManagement />} />
        <Route path="campaigns" element={<AdminCampaigns />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="homepage" element={<AdminHomepage />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="combos" element={<AdminCombos />} />
        <Route path="schemes" element={<AdminSchemes />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="scheme-analytics" element={<AdminSchemeAnalytics />} />
        <Route path="coupon-analytics" element={<AdminCouponAnalytics />} />
        <Route path="loyalty-schemes" element={<AdminLoyaltySchemes />} />
        <Route path="finance" element={<AdminFinance />} />
        <Route path="client-errors" element={<AdminClientErrors />} />
        <Route path="cnf-agents" element={<AdminCnfAgents />} />
        <Route path="account-deletion-requests" element={<AdminAccountDeletionRequests />} />
        <Route path="marketing-budget" element={<AdminMarketingBudget />} />
      </Route>

      {/* Employee Routes */}
      <Route path="/employee/login" element={<EmployeeLoginPage />} />
      
      <Route path="/employee" element={<EmployeeLayout />}>
        <Route index element={<Navigate to="/employee/dashboard" replace />} />
        <Route path="dashboard" element={<EmployeeDashboard />} />
        <Route path="dealers" element={<EmployeeDealers />} />
        <Route path="dealers/:id" element={<EmployeeDealerDetail />} />
        <Route path="orders" element={<EmployeeOrders />} />
        <Route path="orders/:orderId/edit" element={<EmployeeEditOrder />} />
        <Route path="place-order/:dealerId" element={<EmployeePlaceOrder />} />
        <Route path="approvals" element={<EmployeePendingApprovals />} />
        <Route path="incentives" element={<EmployeeIncentives />} />
        <Route path="team" element={<EmployeeTeam />} />
        <Route path="attendance" element={<EmployeeAttendance />} />
        <Route path="liquidation" element={<EmployeeLiquidation />} />
        <Route path="field-visits" element={<EmployeeFieldVisits />} />
        <Route path="shop-visits" element={<EmployeeShopVisits />} />
        <Route path="stocks" element={<EmployeeStocks />} />
        <Route path="targets" element={<EmployeeMyTargets />} />
        <Route path="special-incentive" element={<EmployeeSpecialIncentive />} />
        <Route path="activity-requests" element={<EmployeeActivityRequests />} />
        <Route path="cnf" element={<EmployeeCnfDashboard />} />
      </Route>
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            <AppRoutes />
            <Toaster 
              position="top-center" 
              richColors 
              toastOptions={{
                style: {
                  background: 'white',
                  border: '1px solid #e5e5e5'
                }
              }}
            />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
